/**
 * Shared task pills.
 *
 * Two filled-pill components used by both the Kanban TaskCard and the
 * Tasks Table rows so the type and priority chips render identically
 * across every surface. PR #38 lifted these out of TaskCard.tsx so the
 * table view can show coloured pills instead of mono labels.
 *
 * Both pills share the same size, weight, and shape tokens:
 *   - `inline-flex items-center` so the chip lines up with adjacent
 *     mono labels in a flex row
 *   - 1px outline border in the same colour as the fill so the chip
 *     reads as a solid block against either the dark card surface or
 *     the dark shell
 *   - 12px horizontal padding (`px-mx-3`) and 3px vertical padding,
 *     matching the original TaskCard chip
 *   - body-sm text, 600 weight, uppercase + 0.05em tracking so the
 *     value reads like a chip rather than body copy
 *
 * The pill is presentational only — it never opens menus or commits
 * edits. Inline-edit surfaces (the Tasks Table) keep the underlying
 * `<select>` and tint that select with the same `bg-` token so the
 * editor looks like a pill while staying keyboard-accessible.
 */

import type { TaskPriority, TaskType } from '@workspace/shared';

import { cn } from '@/lib/utils';

/**
 * Background-colour map for the type pill. Lifted from TaskCard in
 * PR #38 so the table can share the same source of truth. GENERAL
 * stays understated on a neutral grey so it does not compete with the
 * named types; the rest pull from the `mx-type-*` tokens defined in
 * the Tailwind config.
 */
export const TYPE_COLOR: Record<TaskType, string> = {
  SALES: 'bg-mx-type-sales text-mx-white border-mx-type-sales',
  ADMIN: 'bg-mx-type-admin text-mx-white border-mx-type-admin',
  FINANCE: 'bg-mx-type-finance text-mx-white border-mx-type-finance',
  PROJECT: 'bg-mx-type-project text-mx-white border-mx-type-project',
  PERSONAL: 'bg-mx-type-personal text-mx-white border-mx-type-personal',
  GENERAL: 'bg-mx-grey-800 text-mx-grey-300 border-mx-grey-800',
};

/**
 * Background-colour map for the priority pill. PR #38 swapped the
 * inline `bg-[#...]` hexes for proper Tailwind tokens (`mx-priority-*`)
 * so the palette overhaul lives in a single place. The pill always
 * renders on white text so the priority semantic carries through on
 * every surface tone.
 */
export const PRIORITY_COLOR: Record<TaskPriority, string> = {
  CRITICAL: 'bg-mx-priority-critical text-mx-white border-mx-priority-critical',
  HIGH: 'bg-mx-priority-high text-mx-white border-mx-priority-high',
  MEDIUM: 'bg-mx-priority-medium text-mx-white border-mx-priority-medium',
  LOW: 'bg-mx-priority-low text-mx-white border-mx-priority-low',
};

/** Shared chip geometry — same as the original TaskCard type pill. */
const PILL_BASE =
  'inline-flex items-center border px-mx-3 py-[3px] text-mx-body-sm font-semibold uppercase tracking-wider';

interface TypePillProps {
  value: TaskType;
  /** Optional extra classes (e.g. test ids in story renders). */
  className?: string;
}

/**
 * Filled type pill (SALES, ADMIN, FINANCE, PROJECT, PERSONAL, GENERAL).
 *
 * Rendered as a span so the pill can sit inside a flex row of other
 * chips without breaking the layout. The pill exposes the value as its
 * label text in upper case — the TaskType enum values are already
 * upper-case strings so no transform is needed.
 */
export function TypePill({ value, className }: TypePillProps) {
  return (
    <span
      data-testid="type-pill"
      data-type={value}
      className={cn('mono-label', PILL_BASE, TYPE_COLOR[value], className)}
    >
      {value}
    </span>
  );
}

interface PriorityPillProps {
  value: TaskPriority;
  className?: string;
}

/**
 * Filled priority pill (CRITICAL, HIGH, MEDIUM, LOW).
 *
 * Mirrors TypePill geometry so the two chips stack consistently inside
 * a row. The colour carries the priority semantic; the text stays
 * white on every variant so the value reads at a glance regardless of
 * the background tone.
 */
export function PriorityPill({ value, className }: PriorityPillProps) {
  return (
    <span
      data-testid="priority-pill"
      data-priority={value}
      className={cn('mono-label', PILL_BASE, PRIORITY_COLOR[value], className)}
    >
      {value}
    </span>
  );
}
